import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { Document, PatchedPatchedDocument } from '../../../js/api';
import { parseApiLocalDate, parseFormDate } from '../utils/date';
import { Button } from './button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from './dialog';
import { FieldValidationErrorMessage } from './field-validation-error-message';
import { Field, Fieldset, Label } from './fieldset';
import { Input } from './input';
import 'react-datepicker/dist/react-datepicker.css';

type DocumentEditDialogProps = {
  open: boolean;
  doc: Document | null;
  onClose: () => void;
  /**
   * Called with the PatchedPatchedDocument payload (label, issued_on,
   * expires_on). The caller owns the mutation.
   */
  onSubmit: (payload: PatchedPatchedDocument) => Promise<unknown>;
  isSubmitting?: boolean;
};

const editFormSchema = z
  .object({
    label: z.string().min(1, 'Name is required'),
    issued_on: z.date().nullable(),
    expires_on: z.date().nullable(),
  })
  .superRefine((val, ctx) => {
    if (val.issued_on && val.expires_on && val.expires_on <= val.issued_on) {
      ctx.addIssue({
        path: ['expires_on'],
        code: z.ZodIssueCode.custom,
        message: 'Expiration date must be after the issue date',
      });
    }
  });

type EditFormData = z.infer<typeof editFormSchema>;

export function DocumentEditDialog({
  open,
  doc,
  onClose,
  onSubmit,
  isSubmitting = false,
}: DocumentEditDialogProps) {
  const isExpirable = !!doc?.document_type?.is_expirable;

  const form = useForm<EditFormData>({
    resolver: zodResolver(editFormSchema),
    mode: 'onChange',
    defaultValues: {
      label: '',
      issued_on: null,
      expires_on: null,
    },
  });

  // Reset form whenever a new document is selected or the dialog reopens.
  useEffect(() => {
    if (open && doc) {
      form.reset({
        label: doc.label || doc.original_filename || '',
        issued_on: parseApiLocalDate(doc.issued_on) ?? null,
        expires_on: parseApiLocalDate(doc.expires_on) ?? null,
      });
    }
  }, [open, doc, form]);

  const handleClose = () => {
    form.reset({ label: '', issued_on: null, expires_on: null });
    onClose();
  };

  const handleSubmit = form.handleSubmit(async (data) => {
    // State-dependent required-field check for expirable document types.
    if (isExpirable) {
      let hasError = false;
      if (!data.issued_on) {
        form.setError('issued_on', { message: 'Issue date is required' });
        hasError = true;
      }
      if (!data.expires_on) {
        form.setError('expires_on', { message: 'Expiration date is required' });
        hasError = true;
      }
      if (hasError) return;
    }
    await onSubmit({
      label: data.label.trim(),
      issued_on: data.issued_on ? parseFormDate(data.issued_on) : null,
      expires_on: data.expires_on ? parseFormDate(data.expires_on) : null,
    });
  });

  return (
    <Dialog open={open} onClose={handleClose} size="xl">
      <DialogTitle className="tw:!text-xl tw:!text-brand-green-0">Edit Document</DialogTitle>
      <DialogBody>
        <form onSubmit={handleSubmit}>
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:gap-4">
              <Field>
                <Label>
                  Name <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Input
                  type="text"
                  {...form.register('label')}
                  disabled={isSubmitting}
                  placeholder="Document Name"
                />
                {form.formState.errors.label && (
                  <FieldValidationErrorMessage message={form.formState.errors.label.message} />
                )}
              </Field>
              <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                <Field>
                  <Label>
                    Issue Date
                    {isExpirable && <span className="tw:text-stoplight-danger"> *</span>}
                  </Label>
                  <Controller
                    name="issued_on"
                    control={form.control}
                    render={({ field }) => (
                      <ReactDatePicker
                        className="datepicker-input"
                        wrapperClassName="datepicker-wrapper"
                        selected={field.value ?? null}
                        onChange={(date) => field.onChange(date)}
                        placeholderText="Select issue date"
                        disabled={isSubmitting}
                        dateFormat="MM/dd/yyyy"
                        isClearable={!isExpirable}
                      />
                    )}
                  />
                  {form.formState.errors.issued_on && (
                    <FieldValidationErrorMessage
                      message={form.formState.errors.issued_on.message}
                    />
                  )}
                </Field>
                <Field>
                  <Label>
                    Expiration Date
                    {isExpirable && <span className="tw:text-stoplight-danger"> *</span>}
                  </Label>
                  <Controller
                    name="expires_on"
                    control={form.control}
                    render={({ field }) => (
                      <ReactDatePicker
                        className="datepicker-input"
                        wrapperClassName="datepicker-wrapper"
                        selected={field.value ?? null}
                        onChange={(date) => field.onChange(date)}
                        placeholderText="Select expiration date"
                        disabled={isSubmitting}
                        dateFormat="MM/dd/yyyy"
                        isClearable={!isExpirable}
                      />
                    )}
                  />
                  {form.formState.errors.expires_on && (
                    <FieldValidationErrorMessage
                      message={form.formState.errors.expires_on.message}
                    />
                  )}
                </Field>
              </div>
            </div>
          </Fieldset>
        </form>
      </DialogBody>
      <DialogActions>
        <Button color="light" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="brandgreen0"
          onClick={handleSubmit}
          disabled={isSubmitting || !form.formState.isValid}
          data-testid="document-edit-submit"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
